<script lang="ts">
  // ActionPrompt — pill flutuante que diz pro user o que fazer:
  //   "sua vez" / "aguardando oponente" / "TRUCO! responda" / "vitoria"
  // Cor varia por contexto.
  type Mode = 'mine' | 'theirs' | 'truco-me' | 'truco-opp' | 'finished-win' | 'finished-lose' | 'idle';

  type Props = { mode: Mode; text: string; deadline?: string | null };
  let { mode, text, deadline = null }: Props = $props();

  // Countdown opcional ao lado do texto. Usado em truco-me (aguardando
  // resposta do oponente) pra mostrar quanto tempo falta antes do
  // auto-refuse acionar. Tick a cada 250ms enquanto deadline esta
  // ativo. Bug reportado pelo Leonardo 2026-05-19: "qnd alguem pede
  // truco n aparece o timer".
  import { onDestroy } from 'svelte';
  let now = $state(Date.now());
  let tickId: number | null = null;
  $effect(() => {
    if (deadline) {
      if (tickId === null) {
        tickId = window.setInterval(() => (now = Date.now()), 250);
      }
    } else if (tickId !== null) {
      clearInterval(tickId);
      tickId = null;
    }
  });
  onDestroy(() => {
    if (tickId !== null) clearInterval(tickId);
  });
  let deadlineMs = $derived(deadline ? new Date(deadline).getTime() : 0);
  let remainingSec = $derived(
    deadlineMs ? Math.max(0, Math.ceil((deadlineMs - now) / 1000)) : 0,
  );
</script>

<div class="prompt" data-mode={mode}>
  <span class="ico">◆</span>
  <span class="text">{text}</span>
  {#if remainingSec > 0}
    <span class="timer" class:danger={remainingSec <= 5}>{remainingSec}s</span>
  {/if}
</div>

<style>
  .prompt {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 6px 14px;
    border-radius: 999px;
    background: linear-gradient(135deg, rgba(14, 10, 44, 0.92), rgba(6, 4, 26, 0.96));
    border: 1px solid var(--border);
    color: var(--text-dim);
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 1.5px;
    text-transform: uppercase;
    font-family: ui-monospace, "SFMono-Regular", Menlo, monospace;
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.04);
    transition: border-color 0.3s, color 0.3s, box-shadow 0.3s;
    white-space: nowrap;
  }
  .ico { font-size: 13px; transition: color 0.3s; }

  .prompt[data-mode='mine'] {
    border-color: rgba(108, 255, 189, 0.6);
    color: var(--ok);
    box-shadow: 0 0 14px rgba(108, 255, 189, 0.35), inset 0 1px 0 rgba(255, 255, 255, 0.06);
  }
  .prompt[data-mode='mine'] .ico { color: var(--ok); }

  .prompt[data-mode='theirs'] {
    border-color: rgba(180, 200, 240, 0.3);
    color: var(--text-dim);
  }

  .prompt[data-mode='truco-me'] {
    border-color: rgba(255, 210, 74, 0.6);
    color: var(--warn);
    box-shadow: 0 0 14px rgba(255, 210, 74, 0.35);
    animation: pulse-warn 1.4s ease-in-out infinite;
  }
  .prompt[data-mode='truco-opp'] {
    border-color: rgba(255, 76, 126, 0.6);
    color: var(--danger);
    box-shadow: 0 0 14px rgba(255, 76, 126, 0.35);
    animation: pulse-danger 1.4s ease-in-out infinite;
  }
  @keyframes pulse-warn {
    0%, 100% { box-shadow: 0 0 14px rgba(255, 210, 74, 0.35); }
    50% { box-shadow: 0 0 22px rgba(255, 210, 74, 0.65); }
  }
  @keyframes pulse-danger {
    0%, 100% { box-shadow: 0 0 14px rgba(255, 76, 126, 0.35); }
    50% { box-shadow: 0 0 22px rgba(255, 76, 126, 0.65); }
  }

  .prompt[data-mode='finished-win'] {
    border-color: rgba(108, 255, 189, 0.7);
    color: var(--ok);
  }
  .prompt[data-mode='finished-lose'] {
    border-color: rgba(255, 76, 126, 0.6);
    color: var(--danger);
  }

  .timer {
    font-family: ui-monospace, "SFMono-Regular", Menlo, monospace;
    font-weight: 800;
    font-size: 11px;
    letter-spacing: 0.5px;
    padding: 2px 6px;
    border-radius: 4px;
    background: rgba(255, 255, 255, 0.06);
    transition: color 0.2s;
  }
  .timer.danger {
    color: var(--danger);
    background: rgba(255, 76, 126, 0.18);
    animation: timer-pulse 0.6s ease-in-out infinite;
  }
  @keyframes timer-pulse {
    0%, 100% { transform: scale(1); }
    50% { transform: scale(1.1); }
  }
</style>
